import { useState, useEffect, useRef, useMemo, useCallback, type KeyboardEvent, type ChangeEvent } from 'react';

import { Chip, Box, Typography, Stack, Divider, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useStylesForwardEmails } from 'modules/ClientCommunication/components/ForwardEmails/ForwardEmails.styles';

import { highlightMatch } from 'helpers/highlightMatched';

import { RegexConstants } from 'constants/regex.constants';

import type { User } from 'types/auth.types';

const validateEmail = (email: string): boolean => RegexConstants.email.test(String(email).toLowerCase());

interface IEmailChipInputProps {
    isEditable: boolean;
    changeable?: boolean;
    label: string;
    emails: string[];
    onChange: (emails: string[]) => void;
    placeholder?: string;
    isCCActive?: boolean;
    isBCCActive?: boolean;
    onActivateEmails?: (label: 'cc' | 'bcc', activate: boolean) => void;
    suggestedUsers: User[];
    getNameByEmail: (email: string) => string | undefined;
    hasError: (error: boolean) => void;
}

export default function EmailChipInput({
  isEditable,
  changeable = true,
  label,
  emails,
  onChange,
  placeholder = 'Add Email',
  isCCActive, isBCCActive,
  onActivateEmails,
  suggestedUsers, getNameByEmail,
  hasError,
}: IEmailChipInputProps) {
  const styles = useStylesForwardEmails();

  const inputContainerRef = useRef<HTMLDivElement>(null);

  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const [isInputActive, setIsInputActive] = useState(changeable);

  useEffect(() => {
    hasError(Boolean(error));
  }, [error, hasError]);

  const addEmail = useCallback((email: string) => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError(null);
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (emails.includes(trimmedEmail)) {
      setError('Email already added');
      return;
    }

    const newEmails = [...emails, trimmedEmail];
    onChange([...emails, trimmedEmail]);
    setInputValue('');
    setError(null);
    hasError(false);
    setIsInputActive(newEmails.length < 5);
  }, [emails, hasError, onChange]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const focused = Boolean(inputContainerRef.current &&
            inputContainerRef.current.contains(event.target as Node));
    setIsFocused(focused);

    if (!focused) {
      addEmail(inputValue);
    }
  }, [addEmail, inputValue]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleSelectEmail = (email: string) => () => {
    addEmail(email);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addEmail(inputValue);
    } else if (event.key === 'Backspace' && !inputValue && emails.length > 0) {
      onChange(emails.slice(0, -1));
    }
  };

  const handleDelete = (emailToDelete: string) => {
    const newEmails = emails.filter(email => email !== emailToDelete);
    onChange(newEmails);
    setIsInputActive(newEmails.length < 5);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    if (!event.target.value.trim()) {
      hasError(false);
    } else {
      hasError(!validateEmail(event.target.value) || emails.includes(event.target.value));
    }
    if (error) {
      setError(null);
    }
  };

  const emailNames = useMemo(() => emails.map((email) => {
    const name = getNameByEmail(email);

    if (name) {
      return {
        name, email,
      };
    }

    return { email };
  }), [emails, getNameByEmail]);

  const usersList = useMemo(() =>
    suggestedUsers.filter(({ email }) => email.toLowerCase().includes(inputValue.toLowerCase())),
  [inputValue, suggestedUsers]);

  const CCAndBCCActivateButtons = () => {
    if ((isCCActive && isBCCActive) || label !== 'To') {
      return null;
    }
    return (
      <Stack direction="row" gap={1.5} justifyItems="center" divider={<Divider orientation="vertical" flexItem />} sx={{ marginLeft: 'auto' }}>
        {!isCCActive && (
          <Button
            variant="text"
            sx={{
              minWidth: 'initial',
              padding: 0,
              color: '#F69C2C',
              lineHeight: 1.5,
              '&:hover': {
                background: 'transparent',
              },
            }}
            onClick={() => onActivateEmails?.('cc', true)}
          >
            CC
          </Button>
        )
        }
        {!isBCCActive && (
          <Button variant="text"
            sx={{
              minWidth: 'initial',
              padding: 0,
              color: '#F69C2C',
              lineHeight: 1.5,
              '&:hover': {
                background: 'transparent',
              },
            }}
            onClick={() => onActivateEmails?.('bcc', true)}
          >
            BCC
          </Button>
        )}
      </Stack>
    );
  };

  if (!isEditable) {
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            borderBottom: '1px solid hsl(var(--border))',
            '&:hover': {
              backgroundColor: 'hsl(var(--accent) / 0.3)',
            },
            transition: 'background-color 0.2s ease',
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              color: '#F69C2C',
              fontWeight: 500,
            }}
          >
            {label}:
          </Typography>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0 8px',
              fontSize: 16,
              color: '#3F5A71',
              fontWeight: 400,
              lineHeight: 1.5,
              letterSpacing: -0.3,
            }}
          >
            {emailNames.map((person, index, emailList) =>
              <span key={person.email}>{(person.name ? `${person.name} (${person.email})` : person.email)}{index === emailList.length - 1 ? '' : ','}</span>,
            )}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={inputContainerRef} sx={{ position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          padding: '8px 16px',
          border: '1px solid #D9D9D9',
          borderRadius: '8px',
          transition: 'background-color 0.2s ease',
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            color: '#F69C2C',
            fontWeight: 500,
            lineHeight: '34px',
          }}
        >
          {label}:
        </Typography>

        <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          {emailNames.map((person, index) => (
            <Chip
              key={`${person.email}-${index}`}
              label={(person.name ? `${person.name} (${person.email})` : person.email)}
              onDelete={changeable ? () => handleDelete(person.email) : undefined}
              deleteIcon={<CloseIcon fontSize="medium" />}
              sx={{
                background: 'transparent',
                padding: '4px 8px',
                height: 'auto',
                color: '#444',
                borderRadius: 15,
                border: '1px solid #D9D9D9',
                fontSize: 16,
                fontWeight: 400,
                lineHeight: 1.5,
                transition: 'all 0.2s ease',
                '& .MuiChip-label': {
                  px: 0,
                },
                '& .MuiChip-deleteIcon': {
                  margin: '0 0 0 4px',
                  color: '#B7B7B7',
                  fontSize: '16px',
                  transition: 'color 0.2s ease',
                },
              }}
            />
          ))}

          {changeable && isInputActive && (
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              placeholder={emails.length === 0 ? placeholder : ''}
              style={{
                border: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
                fontSize: '16px',
                padding: 0,
                lineHeight: '34px',
                flex: '1 1 0%',
                color: 'hsl(var(--foreground))',
                fontFamily: 'inherit',
              }}
            />
          )}
          {CCAndBCCActivateButtons()}
        </Box>
      </Box>
      {inputValue && isFocused && Boolean(usersList.length) && (
        <ul
          className={styles.fsmsList}
          ref={(node) => {
            if (!node) {
              return;
            }
            node.style.top = 'initial';
            node.style.bottom = `-${String(node.getBoundingClientRect().height + 8)}px`;
          }}
        >
          {usersList.map(user => (
            <li
              key={user.email}
              style={{ color: '#444' }}
              onClick={handleSelectEmail(user.email)}
            >
              <Stack gap={1}>
                <span>{user.name}</span>
                <span>
                  {highlightMatch(user.email, inputValue)}
                </span>
              </Stack>
            </li>
          ))}
        </ul>
      )}

      {emails.length === 5 && (
        <Typography
          color="error"
          sx={{
            fontSize: '12px',
            mt: 0.5,
            ml: 7,
          }}
        >
          Maximum of 5 email addresses allowed in the CC field.
        </Typography>
      )}

      {error && (
        <Typography
          color="error"
          sx={{
            fontSize: '12px',
            mt: 0.5,
            ml: 7,
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};
